import { useState } from 'react';
import type { LeadStatus } from '@up2stay/up2promo-api-client';
import {
  LeadListScope,
  LeadStatus as LeadStatusEnum,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  Checkbox,
  CountPill,
  Skeleton,
} from '@up2stay/up2promo-ui';
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowDownUp,
  Calendar,
  Lock,
  Plus,
  ScanLine,
  Search,
  SlidersHorizontal,
  UserRound,
  Users,
} from 'lucide-react';

import { LeadCardList } from '@/core/components/leads/lead-card-list';
import { leadAssigneePickerLabelsFromDict } from '@/core/components/leads/lead-assignee-picker-labels';
import { isLeadArchived } from '@/core/lib/lead-mutate-access';
import { LeadsBulkBar } from '@/core/components/leads/leads-bulk-bar';
import {
  countActiveLeadsOverflowFilters,
  EMPTY_LEADS_LIST_FILTERS,
} from '@/core/components/leads/lead-filter-normalize';
import { LeadsListFilters } from '@/core/components/leads/leads-list-filters';
import { LeadsListSortSelect } from '@/core/components/leads/leads-list-sort-select';
import {
  LeadsActiveFilterChips,
  LeadsMobileFilterSheet,
  leadsMobileFilterActiveCount,
  leadsMobileFilterTriggerClass,
} from '@/core/components/leads/leads-mobile-filter-sheet';
import { LeadsListTable } from '@/core/components/leads/leads-list-table';
import { LeadsScopeTabs } from '@/core/components/leads/leads-scope-tabs';
import { LeadsSearchField } from '@/core/components/leads/leads-search-field';
import { LeadsStatusPills } from '@/core/components/leads/leads-status-pills';
import type { UseLeadsPageResult } from '@/core/components/leads/use-leads-page';
import {
  FilterChipOption,
  FilterChipPopover,
  ListEmptyState,
  ListPageBody,
} from '@/core/components/layout';
import { leadsListSearchInputClass } from '@/core/components/layout/list-toolbar-styles';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';

type LeadsListTabProps = {
  page: UseLeadsPageResult;
  /** When false, scope/view chrome lives on the parent page. */
  showScopeRow?: boolean;
};

export function LeadsListTab({ page, showScopeRow = true }: LeadsListTabProps) {
  const user = useAuthStore((s) => s.user);
  const isMobile = useIsMobile();
  const locale = useLocale();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileFilterEpoch, setMobileFilterEpoch] = useState(0);
  const {
    dict,
    profile,
    currentWorkspace,
    leads,
    leadsError,
    isLoading,
    searchQuery,
    setSearchQuery,
    includeArchived,
    setIncludeArchived,
    workspaceLeadsAlwaysShared,
    listSort,
    setListSort,
    sortLabels,
    leadFilters,
    setLeadFilters,
    filterLabels,
    mobileFilterLabels,
    hasActiveLeadFilters,
    assigneeFilterOptions,
    tagFilterOptions,
    eventFilterOptions,
    canSeeOthersPrivate,
    listScope,
    setListScope,
    scopeCounts,
    scopeLabels,
    canUpdateLeads,
    canExportLeads,
    eventsEnabled,
    isExporting,
    filteredLeads,
    activeSelectedLeadIds,
    allFilteredSelected,
    someFilteredSelected,
    removableTagsForSelection,
    removableEventIdsForSelection,
    bulkEventOptions,
    bulkAssignmentCountsByUserId,
    bulkPrivateShareCount,
    bulkDict,
    bulkUpdateMutation,
    bulkArchiveMutation,
    toggleLeadSelect,
    toggleSelectAllFiltered,
    clearSelection,
    handleExportSelected,
    exportLeads,
    handleLeadClick,
    handleLeadCreated,
    highlightedLeadId,
    handleRefresh,
    resolveLeadCardMeta,
    leadCardLabelMaps,
    setBusinessCardScanOpen,
    createRequestId,
    requestCreateLead,
    searchRequestId,
    statusCounts,
  } = page;

  const listDict = dict.leads.list as Record<string, unknown> & {
    search: string;
    searchClear: string;
    searchCancel: string;
    searchRecent: string;
    searchQuickFilters: string;
    searchLeadsHeading: string;
    searchCompaniesHeading: string;
    searchCompanyPrefix: string;
    searchCompanyFilterHint: string;
    searchCompanyFilterHintPlural: string;
    searchNoteExcerpt: string;
    searchAssignedToMe: string;
    searchStatusNew: string;
    searchShowAllInList: string;
    searchChooseHint: string;
    searchOpenHint: string;
    searchEmptyTitle: string;
    searchDidYouMean: string;
    searchResetFilters: string;
    searchInWorkspace: string;
    searchOthersPrivateHint: string;
    searchMatchReasons: Record<string, string>;
    emptyMineTitle: string;
    emptyMineDescription: string;
    emptyMineCta: string;
    emptyWorkspaceOnlyPrivateTitle: string;
    emptyWorkspaceOnlyPrivateDescription: string;
    emptyWorkspaceOnlyPrivateDescriptionOne: string;
    emptyWorkspaceOnlyPrivateCta: string;
    emptyOthersPrivateTitle: string;
    emptyOthersPrivateDescription: string;
    emptyWorkspaceTitle: string;
    emptyWorkspaceDescription: string;
    emptyWorkspaceScan: string;
    emptyWorkspaceAdd: string;
    filter: string;
    statusFilterAll?: string;
    facetEvent?: string;
    facetAssignee?: string;
    facetMore?: string;
    footerCount?: string;
    footerCountOne?: string;
    footerArchived?: string;
    columns?: Record<string, string>;
    source: Record<string, string>;
    scope?: Record<string, string>;
    quickActions?: {
      menuLabel: string;
      open: string;
      setStatus: string;
      sms: string;
      save: string;
    };
    contextMenu?: {
      menuLabel: string;
      bulkTitle: string;
      assign: string;
      exportCsv: string;
      deleteN: string;
      saveToContacts: string;
      downloadVCard: string;
      updateError: string;
      deleteError: string;
      archiveError: string;
      unarchiveError: string;
      copyError: string;
    };
  };

  const statusLabels = dict.leads.status as Record<string, string>;
  const sourceLabels = listDict.source;
  const columns = listDict.columns ?? {};
  const detailDict = dict.leads.detail;
  const ctxDict = listDict.contextMenu;
  const selectedLeadsForMenu = filteredLeads.filter((lead) =>
    activeSelectedLeadIds.has(lead.id),
  );
  const bulkSelectionAllArchived =
    selectedLeadsForMenu.length > 0 &&
    selectedLeadsForMenu.every((lead) => isLeadArchived(lead));
  const contextMenuProps =
    currentWorkspace?.id && ctxDict
      ? {
          workspaceId: currentWorkspace.id,
          locale,
          selectedLeadIds: activeSelectedLeadIds,
          selectedLeads: selectedLeadsForMenu,
          assigneePickerLabels: leadAssigneePickerLabelsFromDict(
            detailDict.assigneePicker,
          ),
          existingTags: tagFilterOptions,
          canExport: canExportLeads,
          workspaceLeadsAlwaysShared,
          onExportLeads: exportLeads,
          onRefresh: handleRefresh,
          labels: {
            menuLabel: ctxDict.menuLabel,
            bulkTitle: ctxDict.bulkTitle,
            open: listDict.quickActions?.open ?? dict.leads.create.openDetails,
            status: dict.leads.list.bulk?.setStatus ?? 'Status',
            assignee: columns.assignee ?? 'Assignee',
            assign: ctxDict.assign,
            tags: detailDict.tags,
            archive: detailDict.archive,
            unarchive: detailDict.unarchive,
            delete: detailDict.delete,
            deleteN: ctxDict.deleteN,
            saveToContacts: ctxDict.saveToContacts,
            downloadVCard: ctxDict.downloadVCard,
            exportCsv: ctxDict.exportCsv,
            call: detailDict.call,
            email: detailDict.email,
            sms: detailDict.sms,
            copy: detailDict.copy,
            tagPlaceholder: detailDict.tagPlaceholder,
            applyTag: detailDict.addTagButton,
            existingTags: detailDict.existingTags,
            noExistingTags: detailDict.noExistingTags,
            cancel: detailDict.cancel,
            deleteConfirmTitle: detailDict.deleteConfirmTitle,
            deleteConfirmDescription: detailDict.deleteConfirmDescription,
            deleteConfirm: detailDict.deleteConfirm,
            saving: dict.leads.list.bulk?.updating ?? 'Updating…',
            updateError: ctxDict.updateError,
            deleteError: ctxDict.deleteError,
            archiveError: ctxDict.archiveError,
            unarchiveError: ctxDict.unarchiveError,
            copyError: ctxDict.copyError,
            saveEmptyError: detailDict.saveToDevice.toastEmpty,
            saveFailedError: detailDict.saveToDevice.toastFailed,
          },
        }
      : undefined;

  const toggleStatus = (status: LeadStatus) => {
    const current = leadFilters.statuses;
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    setLeadFilters({ ...leadFilters, statuses: next });
  };

  const sheetFilterCount = leadsMobileFilterActiveCount(
    leadFilters,
    includeArchived,
  );
  const overflowFilterCount = countActiveLeadsOverflowFilters(leadFilters);

  const searchField = (
    <LeadsSearchField
      className="min-w-[220px] flex-1"
      inputClassName={leadsListSearchInputClass}
      showSlashHint={false}
      hideIdleTrigger={isMobile}
      openRequestId={searchRequestId}
      workspaceId={currentWorkspace?.id}
      scope={listScope}
      value={searchQuery}
      onChange={setSearchQuery}
      onCommit={setSearchQuery}
      onOpenLead={(leadId) => {
        const lead = leads.find((item) => item.id === leadId);
        if (lead) handleLeadClick(lead);
      }}
      onQuickFilterAssignedToMe={() => {
        const uid = user?.id?.trim();
        if (!uid) return;
        setLeadFilters({
          ...leadFilters,
          assigneeUserIds: [uid],
        });
      }}
      onQuickFilterStatusNew={() => {
        setLeadFilters({
          ...leadFilters,
          statuses: [LeadStatusEnum.NEW],
        });
      }}
      onQuickFilterEvent={(eventId) => {
        setLeadFilters({
          ...leadFilters,
          eventIds: [eventId],
        });
      }}
      eventQuickFilters={(eventFilterOptions ?? [])
        .slice(0, 2)
        .map((opt) => ({ id: opt.id, name: opt.label }))}
      onSwitchToWorkspaceScope={() => setListScope(LeadListScope.WORKSPACE)}
      onScopeChange={setListScope}
      onResetFilters={() => setLeadFilters({ ...EMPTY_LEADS_LIST_FILTERS })}
      scopeCounts={scopeCounts}
      canSeeOthersPrivate={
        canSeeOthersPrivate &&
        currentWorkspace?.leadsAlwaysSharedWithWorkspace !== true
      }
      statusLabels={statusLabels}
      labels={{
        placeholder: listDict.search,
        clearAriaLabel: listDict.searchClear,
        cancelLabel: listDict.searchCancel,
        recentHeading: listDict.searchRecent,
        quickFiltersHeading: listDict.searchQuickFilters,
        leadsHeading: listDict.searchLeadsHeading,
        companiesHeading: listDict.searchCompaniesHeading,
        companyPrefix: listDict.searchCompanyPrefix,
        companyFilterHint: listDict.searchCompanyFilterHint,
        companyFilterHintPlural: listDict.searchCompanyFilterHintPlural,
        noteExcerpt: listDict.searchNoteExcerpt,
        assignedToMe: listDict.searchAssignedToMe,
        statusNew: listDict.searchStatusNew,
        showAllInList: listDict.searchShowAllInList,
        chooseHint: listDict.searchChooseHint,
        openHint: listDict.searchOpenHint,
        emptyTitle: listDict.searchEmptyTitle,
        didYouMean: listDict.searchDidYouMean,
        resetFilters: listDict.searchResetFilters,
        searchInWorkspace: listDict.searchInWorkspace,
        othersPrivateHint: listDict.searchOthersPrivateHint,
        matchReasons: listDict.searchMatchReasons,
        scopeMine: scopeLabels.mine,
        scopeWorkspace: scopeLabels.workspace,
        scopeOthersPrivate: scopeLabels.othersPrivate,
      }}
    />
  );

  const isScopeEmpty =
    !isLoading &&
    filteredLeads.length === 0 &&
    !searchQuery.trim() &&
    !hasActiveLeadFilters;

  const showArchiveAffordance =
    includeArchived || scopeCounts.archivedHidden > 0;

  // DEV-536: "1 Lead", and say "angezeigt" once archived leads are listed.
  const footerCountLabel =
    filteredLeads.length === 1
      ? (listDict.footerCountOne ?? '1 Lead')
      : (listDict.footerCount ?? '{count} Leads').replace(
          '{count}',
          String(filteredLeads.length),
        );
  const archivedLabel = includeArchived
    ? (scopeLabels.archivedShown ?? '{count} archived shown').replace(
        '{count}',
        String(filteredLeads.filter((l) => l.archivedAt).length),
      )
    : (
        scopeLabels.archivedHidden ??
        listDict.footerArchived ??
        '{count} archived hidden'
      ).replace('{count}', String(scopeCounts.archivedHidden));

  // Active list empty → L1/L3/L4. Archive Anzeigen composes under the empty
  // state when archivedHidden > 0 (desktop/mobile already render both).
  const emptyScopeState = isScopeEmpty ? (
    listScope === LeadListScope.MINE && scopeCounts.workspace > 0 ? (
      <ListEmptyState
        icon={<UserRound className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyMineTitle}
        description={listDict.emptyMineDescription.replace(
          '{count}',
          String(scopeCounts.workspace),
        )}
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => setListScope(LeadListScope.WORKSPACE)}
          >
            {listDict.emptyMineCta}
          </Button>
        }
      />
    ) : listScope === LeadListScope.WORKSPACE && scopeCounts.mine > 0 ? (
      <ListEmptyState
        icon={<Lock className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyWorkspaceOnlyPrivateTitle}
        description={
          scopeCounts.mine === 1
            ? listDict.emptyWorkspaceOnlyPrivateDescriptionOne
            : listDict.emptyWorkspaceOnlyPrivateDescription.replace(
                '{count}',
                String(scopeCounts.mine),
              )
        }
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => setListScope(LeadListScope.MINE)}
          >
            {listDict.emptyWorkspaceOnlyPrivateCta}
          </Button>
        }
      />
    ) : listScope === LeadListScope.OTHERS_PRIVATE ? (
      <ListEmptyState
        icon={<Lock className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyOthersPrivateTitle}
        description={listDict.emptyOthersPrivateDescription}
      />
    ) : leads.length === 0 ||
      (listScope === LeadListScope.WORKSPACE && scopeCounts.workspace === 0) ? (
      <ListEmptyState
        icon={<Users className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyWorkspaceTitle}
        description={listDict.emptyWorkspaceDescription}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setBusinessCardScanOpen(true)}
            >
              <ScanLine className="mr-2 h-4 w-4" />
              {listDict.emptyWorkspaceScan}
            </Button>
            <Button type="button" onClick={() => requestCreateLead()}>
              <Plus className="mr-2 h-4 w-4" />
              {listDict.emptyWorkspaceAdd}
            </Button>
          </div>
        }
      />
    ) : null
  ) : null;

  const archiveLine = showArchiveAffordance ? (
    <div className="flex items-center gap-2 px-[18px] py-[11px] text-xs text-muted-foreground">
      <Archive className="size-[13px] shrink-0" aria-hidden />
      <span>
        {footerCountLabel}
        {' · '}
        {archivedLabel}
        {' · '}
        <button
          type="button"
          className="font-semibold text-[color:var(--ghost-accent-fg)]"
          onClick={() => setIncludeArchived(!includeArchived)}
        >
          {includeArchived
            ? (listDict.scope?.hideArchived ?? 'Ausblenden')
            : (listDict.scope?.showArchived ?? 'Anzeigen')}
        </button>
      </span>
    </div>
  ) : (
    <div className="flex items-center gap-2 px-[18px] py-[11px] text-xs text-muted-foreground">
      <span>{footerCountLabel}</span>
    </div>
  );

  return (
    <ListPageBody>
      {leadsError ? (
        <ListEmptyState
          tone="error"
          icon={<AlertCircle className="h-6 w-6" aria-hidden />}
          title={dict.leads.loadError.title}
          description={dict.leads.loadError.description}
          action={
            <Button variant="outline" onClick={handleRefresh}>
              {dict.leads.loadError.retry}
            </Button>
          }
        />
      ) : null}

      {showScopeRow ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <LeadsScopeTabs
            value={listScope}
            onChange={setListScope}
            counts={scopeCounts}
            canSeeOthersPrivate={canSeeOthersPrivate}
            fullWidth={isMobile}
            othersPrivateShortLabel={listDict.scope?.othersPrivateShort}
            labels={{
              mine: scopeLabels.mine,
              workspace: scopeLabels.workspace,
              othersPrivate: scopeLabels.othersPrivate,
            }}
          />
          {workspaceLeadsAlwaysShared ? (
            <p className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
              <Lock className="size-[13px] shrink-0" aria-hidden />
              <span>
                {listDict.scope?.policyShared ??
                  'Richtlinie: Alle Leads werden geteilt'}
                {' · '}
                <ShellLink
                  to={shellPaths.moreWorkspaceSettings}
                  className="font-semibold text-[color:var(--ghost-accent-fg)] underline-offset-2 hover:underline"
                >
                  {listDict.scope?.policySettings ?? 'Einstellungen'}
                </ShellLink>
              </span>
            </p>
          ) : null}
        </div>
      ) : null}

      {listScope === LeadListScope.OTHERS_PRIVATE ? (
        <div className="flex items-start gap-2 rounded-lg border border-[color:var(--warn-border)] bg-[color:var(--warn-bg)] px-3 py-2 text-sm text-[color:var(--warn-fg)]">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            {dict.leads.list.scope?.othersPrivateBanner ??
              scopeLabels.othersPrivate}
          </p>
        </div>
      ) : null}

      {isMobile ? (
        <>
          {searchField}
          <LeadsStatusPills
            selected={leadFilters.statuses}
            counts={statusCounts}
            statusLabels={statusLabels}
            allLabel={listDict.statusFilterAll ?? 'Alle'}
            onSelectAll={() => setLeadFilters({ ...leadFilters, statuses: [] })}
            onToggleStatus={toggleStatus}
            className="-mx-1 px-1"
          />
          <LeadsActiveFilterChips
            value={leadFilters}
            onChange={setLeadFilters}
            includeArchived={includeArchived}
            onIncludeArchivedChange={setIncludeArchived}
            assigneeOptions={assigneeFilterOptions}
            eventOptions={eventFilterOptions}
            labels={{
              showArchived: mobileFilterLabels.showArchived,
              periodLabels: {
                all: mobileFilterLabels.periodAll,
                last_day: mobileFilterLabels.periodLastDay,
                last_week: mobileFilterLabels.periodLastWeek,
                last_month: mobileFilterLabels.periodLastMonth,
              },
              sourceLabels: mobileFilterLabels.sourceLabels,
              removeFilter: mobileFilterLabels.removeFilter,
            }}
          />
          <div className="flex items-center justify-between gap-3 px-0.5 text-[12px] text-muted-foreground">
            <span>
              {footerCountLabel} · {sortLabels.options[listSort]}
            </span>
            <button
              type="button"
              className={leadsMobileFilterTriggerClass(sheetFilterCount > 0)}
              onClick={() => {
                setMobileFilterEpoch((n) => n + 1);
                setMobileFilterOpen(true);
              }}
            >
              <SlidersHorizontal className="size-[13px]" aria-hidden />
              {sheetFilterCount > 0
                ? `${listDict.filter} · ${sheetFilterCount}`
                : listDict.filter}
            </button>
          </div>
          <LeadsMobileFilterSheet
            key={mobileFilterEpoch}
            open={mobileFilterOpen}
            onOpenChange={setMobileFilterOpen}
            value={leadFilters}
            onChange={setLeadFilters}
            listSort={listSort}
            onListSortChange={setListSort}
            includeArchived={includeArchived}
            onIncludeArchivedChange={setIncludeArchived}
            leads={leads}
            searchQuery={searchQuery}
            assigneeOptions={assigneeFilterOptions}
            tagOptions={tagFilterOptions}
            eventOptions={eventFilterOptions}
            labels={mobileFilterLabels}
          />
        </>
      ) : (
        <Card className="gap-0 overflow-hidden border-[color:var(--surface-border)] py-0 shadow-[var(--shadow-card)]">
          {activeSelectedLeadIds.size > 0 ? (
            <div className="border-b border-[color:var(--surface-border)]">
              <LeadsBulkBar
                workspaceId={currentWorkspace?.id ?? ''}
                locale={locale}
                selectedCount={activeSelectedLeadIds.size}
                filteredCount={filteredLeads.length}
                canUpdate={canUpdateLeads}
                canExport={canExportLeads}
                canOrganizeEvents={eventsEnabled}
                isUpdating={bulkUpdateMutation.isPending}
                isExporting={isExporting}
                isArchiving={bulkArchiveMutation.isPending}
                archiveMode={bulkSelectionAllArchived ? 'unarchive' : 'archive'}
                statusLabels={statusLabels}
                assigneePickerLabels={leadAssigneePickerLabelsFromDict(
                  dict.leads.detail.assigneePicker,
                )}
                assignmentCountsByUserId={bulkAssignmentCountsByUserId}
                privateShareCount={bulkPrivateShareCount}
                eventOptions={bulkEventOptions}
                removableEventIds={removableEventIdsForSelection}
                existingTags={tagFilterOptions}
                removableTags={removableTagsForSelection}
                labels={{
                  selected: bulkDict?.selected ?? '{count} selected',
                  selectedOf: bulkDict?.selectedOf ?? 'of {total}',
                  export: bulkDict?.export ?? 'Export selected',
                  setStatus: bulkDict?.setStatus ?? 'Status',
                  setAssignee: bulkDict?.setAssignee ?? 'Assignee',
                  addTags: bulkDict?.addTags ?? 'Tags',
                  removeTags: bulkDict?.removeTags ?? 'Remove tag',
                  addToEvent: bulkDict?.addToEvent ?? 'Event',
                  removeFromEvent:
                    bulkDict?.removeFromEvent ?? 'Remove from event',
                  noEventsToAdd:
                    bulkDict?.noEventsToAdd ?? 'No events available.',
                  noEventsToRemove:
                    bulkDict?.noEventsToRemove ??
                    'No shared events on selection.',
                  clear: bulkDict?.clear ?? 'Clear',
                  archive: bulkDict?.archive ?? 'Archive',
                  unarchive:
                    bulkDict?.unarchive ?? detailDict.unarchive ?? 'Restore',
                  tagPlaceholder: bulkDict?.tagPlaceholder ?? 'Tag name…',
                  applyTag: bulkDict?.applyTag ?? 'Add tag',
                  existingTags: bulkDict?.existingTags ?? 'Existing tags',
                  noExistingTags:
                    bulkDict?.noExistingTags ?? 'No matching tags.',
                  noTagsToRemove:
                    bulkDict?.noTagsToRemove ?? 'No shared tags on selection.',
                  updating: bulkDict?.updating ?? 'Updating…',
                  ownershipHint:
                    bulkDict?.ownershipHint ??
                    '{count} lead is another member’s private record; you need “Update others’ private leads” to change it.',
                  ownershipHintPlural:
                    bulkDict?.ownershipHintPlural ??
                    '{count} leads are other members’ private records; you need “Update others’ private leads” to change them.',
                }}
                othersOwnedSelectedCount={
                  filteredLeads.filter(
                    (l) =>
                      activeSelectedLeadIds.has(l.id) &&
                      l.userId?.trim() &&
                      l.userId !== user?.id,
                  ).length
                }
                onClear={clearSelection}
                onExport={handleExportSelected}
                onArchive={() =>
                  bulkArchiveMutation.mutate(!bulkSelectionAllArchived)
                }
                onSetStatus={(status: LeadStatus) =>
                  bulkUpdateMutation.mutate({ setStatus: status })
                }
                onSetAssignee={(userId) =>
                  bulkUpdateMutation.mutate({ setAssigneeUserId: userId })
                }
                onAddTag={(tag) =>
                  bulkUpdateMutation.mutate({ addTags: [tag] })
                }
                onRemoveTag={(tag) =>
                  bulkUpdateMutation.mutate({ removeTags: [tag] })
                }
                onAddToEvent={(eventId) =>
                  bulkUpdateMutation.mutate({ addEventIds: [eventId] })
                }
                onRemoveFromEvent={(eventId) =>
                  bulkUpdateMutation.mutate({ removeEventIds: [eventId] })
                }
              />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2.5 border-b border-[color:var(--surface-border)] px-[14px] py-3">
              {searchField}
              <LeadsStatusPills
                selected={leadFilters.statuses}
                counts={statusCounts}
                statusLabels={statusLabels}
                allLabel={listDict.statusFilterAll ?? 'Alle'}
                onSelectAll={() =>
                  setLeadFilters({ ...leadFilters, statuses: [] })
                }
                onToggleStatus={toggleStatus}
              />
              <div
                className="hidden h-[22px] w-px bg-[color:var(--surface-border)] sm:block"
                aria-hidden
              />
              {eventsEnabled ? (
                <FilterChipPopover
                  icon={Calendar}
                  label={listDict.facetEvent ?? 'Event'}
                  count={leadFilters.eventIds.length}
                >
                  {(eventFilterOptions ?? []).map((opt) => (
                    <FilterChipOption
                      key={opt.id}
                      selected={leadFilters.eventIds.includes(opt.id)}
                      onToggle={() => {
                        const next = leadFilters.eventIds.includes(opt.id)
                          ? leadFilters.eventIds.filter((id) => id !== opt.id)
                          : [...leadFilters.eventIds, opt.id];
                        setLeadFilters({ ...leadFilters, eventIds: next });
                      }}
                    >
                      {opt.label}
                    </FilterChipOption>
                  ))}
                </FilterChipPopover>
              ) : null}
              <FilterChipPopover
                icon={UserRound}
                label={listDict.facetAssignee ?? 'Zuständig'}
                count={leadFilters.assigneeUserIds.length}
              >
                {assigneeFilterOptions.map((opt) => (
                  <FilterChipOption
                    key={opt.userId}
                    selected={leadFilters.assigneeUserIds.includes(opt.userId)}
                    onToggle={() => {
                      const next = leadFilters.assigneeUserIds.includes(
                        opt.userId,
                      )
                        ? leadFilters.assigneeUserIds.filter(
                            (id) => id !== opt.userId,
                          )
                        : [...leadFilters.assigneeUserIds, opt.userId];
                      setLeadFilters({
                        ...leadFilters,
                        assigneeUserIds: next,
                      });
                    }}
                  >
                    {opt.label}
                  </FilterChipOption>
                ))}
              </FilterChipPopover>
              <LeadsListFilters
                value={leadFilters}
                onChange={setLeadFilters}
                tagOptions={tagFilterOptions}
                labels={filterLabels}
                trigger={
                  <CountPill
                    label={listDict.facetMore ?? 'Mehr'}
                    count={overflowFilterCount || undefined}
                    active={overflowFilterCount > 0}
                  />
                }
              />
              <div className="ml-auto flex min-w-0 items-center text-[12.5px] text-muted-foreground">
                <LeadsListSortSelect
                  value={listSort}
                  onChange={setListSort}
                  labels={sortLabels}
                />
              </div>
            </div>
          )}

          {isLoading && leads.length === 0 ? (
            <div className="flex flex-col gap-0 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 border-b border-[color:var(--surface-border)] py-3 last:border-0"
                >
                  <Skeleton className="size-[34px] rounded-[10px]" />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
              ))}
            </div>
          ) : emptyScopeState ? (
            <div>
              <div className="p-4">{emptyScopeState}</div>
              {showArchiveAffordance ? (
                <div className="border-t border-[color:var(--surface-border)]">
                  {archiveLine}
                </div>
              ) : null}
            </div>
          ) : filteredLeads.length === 0 &&
            (searchQuery.trim() || hasActiveLeadFilters) ? (
            <div>
              <ListEmptyState
                className="border-0 shadow-none"
                icon={<Search className="h-6 w-6 text-muted-foreground" />}
                title={
                  hasActiveLeadFilters && !searchQuery.trim()
                    ? dict.leads.noFilteredResults.title
                    : dict.leads.noResults.title
                }
                description={
                  hasActiveLeadFilters && !searchQuery.trim()
                    ? dict.leads.noFilteredResults.description
                    : dict.leads.noResults.description
                }
                action={
                  listScope !== LeadListScope.WORKSPACE ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setListScope(LeadListScope.WORKSPACE)}
                    >
                      {listDict.searchInWorkspace}
                    </Button>
                  ) : undefined
                }
              />
              {showArchiveAffordance ? (
                <div className="border-t border-[color:var(--surface-border)]">
                  {archiveLine}
                </div>
              ) : null}
            </div>
          ) : (
            <LeadsListTable
              variant="desktop"
              leads={filteredLeads}
              locale={locale}
              searchQuery={searchQuery}
              highlightedLeadId={highlightedLeadId}
              selectionEnabled
              selectedLeadIds={activeSelectedLeadIds}
              onToggleLeadSelect={toggleLeadSelect}
              selectAriaLabel={bulkDict?.selectLead ?? 'Select {name}'}
              onLeadClick={handleLeadClick}
              leadCardMeta={resolveLeadCardMeta}
              labelMaps={leadCardLabelMaps}
              statusLabels={statusLabels}
              sourceLabels={sourceLabels}
              contextMenu={contextMenuProps}
              columnLabels={{
                contact: columns.contact ?? 'Kontakt',
                status: columns.status ?? 'Status',
                origin: columns.origin ?? 'Herkunft',
                assignee: columns.assignee ?? 'Zuständig',
                visibility: columns.visibility ?? 'Sichtbar',
                captured: columns.captured ?? 'Erfasst',
              }}
              headerCheckbox={
                <Checkbox
                  checked={
                    allFilteredSelected
                      ? true
                      : someFilteredSelected
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={() => toggleSelectAllFiltered()}
                  aria-label={bulkDict?.selectAll ?? 'Select all'}
                  className="size-4 rounded-[5px]"
                />
              }
              footer={
                <div className="border-t border-[color:var(--surface-border)]">
                  {archiveLine}
                </div>
              }
            />
          )}
        </Card>
      )}

      {isMobile ? (
        isLoading && leads.length === 0 ? (
          <Skeleton className="h-64 w-full rounded-[var(--radius-card-mobile)]" />
        ) : emptyScopeState ? (
          <div className="flex flex-col gap-2">
            {emptyScopeState}
            {showArchiveAffordance ? (
              <div className="flex items-center gap-2 px-1 pt-1 text-[12px] text-muted-foreground">
                <Archive className="size-[13px]" aria-hidden />
                <span>
                  {archivedLabel}
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-[color:var(--ghost-accent-fg)]"
                    onClick={() => setIncludeArchived(!includeArchived)}
                  >
                    {includeArchived
                      ? (listDict.scope?.hideArchived ?? 'Ausblenden')
                      : (listDict.scope?.showArchived ?? 'Anzeigen')}
                  </button>
                </span>
              </div>
            ) : null}
          </div>
        ) : filteredLeads.length === 0 &&
          (searchQuery.trim() || hasActiveLeadFilters) ? (
          <div className="flex flex-col gap-2">
            <ListEmptyState
              icon={<Search className="h-6 w-6 text-muted-foreground" />}
              title={dict.leads.noResults.title}
              description={dict.leads.noResults.description}
              action={
                listScope !== LeadListScope.WORKSPACE ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setListScope(LeadListScope.WORKSPACE)}
                  >
                    {listDict.searchInWorkspace}
                  </Button>
                ) : undefined
              }
            />
            {showArchiveAffordance ? (
              <div className="flex items-center gap-2 px-1 pt-1 text-[12px] text-muted-foreground">
                <Archive className="size-[13px]" aria-hidden />
                <span>
                  {archivedLabel}
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-[color:var(--ghost-accent-fg)]"
                    onClick={() => setIncludeArchived(!includeArchived)}
                  >
                    {includeArchived
                      ? (listDict.scope?.hideArchived ?? 'Ausblenden')
                      : (listDict.scope?.showArchived ?? 'Anzeigen')}
                  </button>
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <LeadsListTable
              variant="mobile"
              leads={filteredLeads}
              locale={locale}
              searchQuery={searchQuery}
              highlightedLeadId={highlightedLeadId}
              onLeadClick={handleLeadClick}
              leadCardMeta={resolveLeadCardMeta}
              labelMaps={leadCardLabelMaps}
              statusLabels={statusLabels}
              sourceLabels={sourceLabels}
              contextMenu={contextMenuProps}
              columnLabels={{
                contact: columns.contact ?? 'Kontakt',
                status: columns.status ?? 'Status',
                origin: columns.origin ?? 'Herkunft',
                assignee: columns.assignee ?? 'Zuständig',
                visibility: columns.visibility ?? 'Sichtbar',
                captured: columns.captured ?? 'Erfasst',
              }}
            />
            {showArchiveAffordance ? (
              <div className="flex items-center gap-2 px-1 pt-1 text-[12px] text-muted-foreground">
                <Archive className="size-[13px]" aria-hidden />
                <span>
                  {archivedLabel}
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-[color:var(--ghost-accent-fg)]"
                    onClick={() => setIncludeArchived(!includeArchived)}
                  >
                    {includeArchived
                      ? (listDict.scope?.hideArchived ?? 'Ausblenden')
                      : (listDict.scope?.showArchived ?? 'Anzeigen')}
                  </button>
                </span>
              </div>
            ) : null}
          </>
        )
      ) : null}

      <LeadCardList
        leads={[]}
        existingLeads={leads}
        profileId={profile?.id}
        workspaceId={currentWorkspace?.id ?? null}
        onLeadClick={handleLeadClick}
        onLeadCreated={handleLeadCreated}
        onScanBusinessCard={() => setBusinessCardScanOpen(true)}
        hideAddCards
        hideLeadCards
        createRequestId={createRequestId}
      />
    </ListPageBody>
  );
}
